/**
 * Compliance Posture — Integrations sub-section.
 *
 * Admin-only UI for managing GRC tool integration:
 *   - API keys (generate / list / revoke) for OSCAL + evidence endpoints
 *   - Outbound webhooks for compliance.posture.changed events
 *
 * Mounted inside CompliancePosturePage as a collapsible card.
 */
import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Tooltip, Link, Accordion, AccordionSummary, AccordionDetails,
  Divider,
} from '@mui/material'
import {
  ContentCopy, Delete, Add, ExpandMore, VpnKey,
} from '@mui/icons-material'
import { analysisApi } from '../services/api'

interface ApiKey {
  id: string
  label: string
  key_prefix: string
  created_at: string
  last_used_at?: string
  revoked_at?: string
  is_active: boolean
}

interface WebhookEntry {
  id: string
  label: string
  url: string
  created_at: string
  last_delivered_at?: string
  last_status_code?: number
  last_error?: string
  active: boolean
}

interface Props {
  tenantTier: string
  visibility: string
  apiBaseUrl: string  // for showing example URLs to paste into GRC tool
}

const ComplianceIntegrations = ({ tenantTier, visibility, apiBaseUrl }: Props) => {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [webhooks, setWebhooks] = useState<WebhookEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create-key dialog
  const [createKeyOpen, setCreateKeyOpen] = useState(false)
  const [keyLabel, setKeyLabel] = useState('')
  const [newKeyResp, setNewKeyResp] = useState<any>(null)
  const [creating, setCreating] = useState(false)

  // Create-webhook dialog
  const [createWhOpen, setCreateWhOpen] = useState(false)
  const [whLabel, setWhLabel] = useState('')
  const [whUrl, setWhUrl] = useState('')
  const [newWhResp, setNewWhResp] = useState<any>(null)
  const [whCreating, setWhCreating] = useState(false)

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const [kr, wr] = await Promise.allSettled([
        analysisApi.listComplianceApiKeys(),
        analysisApi.listComplianceWebhooks(),
      ])
      if (kr.status === 'fulfilled') setKeys(kr.value.data?.keys || [])
      if (wr.status === 'fulfilled') setWebhooks(wr.value.data?.webhooks || [])
      if (kr.status === 'rejected' && (kr.reason?.response?.status === 403 || kr.reason?.response?.status === 404)) {
        setError('Integrations require Admin role on a Business+addon or Enterprise plan.')
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load integrations')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (visibility === 'full' || visibility === 'teaser') fetchAll()
    else setLoading(false)
  }, [visibility])

  // Don't render at all for tenants who can't use it
  if (visibility !== 'full' && tenantTier !== 'design_partner') return null

  const handleCreateKey = async () => {
    if (!keyLabel.trim()) return
    setCreating(true)
    try {
      const resp = await analysisApi.createComplianceApiKey(keyLabel.trim())
      setNewKeyResp(resp.data)
      setKeyLabel('')
      await fetchAll()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to create API key')
    } finally {
      setCreating(false)
    }
  }

  const handleRevokeKey = async (id: string) => {
    if (!confirm('Revoke this API key? Any GRC tool using it will stop receiving data.')) return
    try {
      await analysisApi.revokeComplianceApiKey(id)
      await fetchAll()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to revoke')
    }
  }

  const handleCreateWebhook = async () => {
    if (!whLabel.trim() || !whUrl.trim()) return
    setWhCreating(true)
    try {
      const resp = await analysisApi.createComplianceWebhook(whLabel.trim(), whUrl.trim())
      setNewWhResp(resp.data)
      setWhLabel('')
      setWhUrl('')
      await fetchAll()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to create webhook')
    } finally {
      setWhCreating(false)
    }
  }

  const handleDeleteWebhook = async (id: string) => {
    if (!confirm('Delete this webhook destination?')) return
    try {
      await analysisApi.deleteComplianceWebhook(id)
      await fetchAll()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to delete')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // intentionally subtle — no toast library; the click feedback is enough
    })
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <VpnKey color="primary" />
          <Typography variant="h6">Integrations</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect ARIAS Compliance Posture to your GRC tool (Vanta, TrustCloud, Drata, OneTrust, Hyperproof, AuditBoard, ServiceNow GRC, Zania) using API keys and webhooks. Read the <Link href="https://tryarias.com/docs/compliance-integrations/" target="_blank" rel="noopener">integration guide</Link>.
        </Typography>

        {error && <Alert severity="info" sx={{ mb: 2 }}>{error}</Alert>}

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>API Keys</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Used by GRC tools to pull posture data from ARIAS
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => setCreateKeyOpen(true)}
              >
                Generate API key
              </Button>
              <Typography variant="caption" color="text.secondary">
                Paste the generated key into your GRC tool's custom integration / data source panel.
              </Typography>
            </Box>

            {keys.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No API keys yet.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell>Prefix</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last used</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {keys.map((k) => (
                      <TableRow key={k.id}>
                        <TableCell>{k.label}</TableCell>
                        <TableCell><code>{k.key_prefix}...</code></TableCell>
                        <TableCell>{new Date(k.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {k.last_used_at
                            ? <Tooltip title={new Date(k.last_used_at).toLocaleString()}><span>{new Date(k.last_used_at).toLocaleDateString()}</span></Tooltip>
                            : <Typography variant="caption" color="text.secondary">never</Typography>}
                        </TableCell>
                        <TableCell>
                          {k.is_active
                            ? <Chip size="small" color="success" label="Active" />
                            : <Chip size="small" label="Revoked" />}
                        </TableCell>
                        <TableCell align="right">
                          {k.is_active && (
                            <Tooltip title="Revoke this key">
                              <IconButton size="small" onClick={() => handleRevokeKey(k.id)}><Delete fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>How GRC tools use these keys</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Configure your GRC tool to pull from ARIAS using the API key as a Bearer token:
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.8rem' }}>
              <Box>GET {apiBaseUrl}/api/analysis/compliance/oscal/eu_ai_act</Box>
              <Box>GET {apiBaseUrl}/api/analysis/compliance/evidence?since=2026-05-01T00:00:00Z</Box>
              <Box sx={{ mt: 1 }}>Authorization: Bearer aria_cp_&lt;your-key&gt;</Box>
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
              OSCAL endpoint returns NIST OSCAL 1.1.2 assessment-results JSON.
              Evidence endpoint returns a delta of compliance assessments for polling-based ingestion.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              <strong>Available framework values:</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {[
                { value: 'eu_ai_act', label: 'EU AI Act' },
                { value: 'nist_ai_rmf', label: 'NIST AI RMF' },
                { value: 'iso_42001', label: 'ISO/IEC 42001' },
                { value: 'soc2', label: 'SOC 2' },
                { value: 'gdpr', label: 'GDPR' },
                { value: 'owasp_llm_top_10', label: 'OWASP LLM Top 10' },
                { value: 'owasp_agentic_top_10', label: 'OWASP Agentic AI Top 10' },
                { value: 'maestro', label: 'MAESTRO' },
              ].map((f) => (
                <Tooltip key={f.value} title={f.label}>
                  <Chip
                    size="small"
                    label={f.value}
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'help' }}
                    onClick={() => copyToClipboard(f.value)}
                  />
                </Tooltip>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Click any framework name to copy. Optional query params: <code>?repository=&lt;name&gt;</code> filters to one repo's latest scan.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Webhooks</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              ARIAS POSTs compliance.posture.changed events as your scans run
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => setCreateWhOpen(true)}
              >
                Add webhook
              </Button>
              <Typography variant="caption" color="text.secondary">
                Receive HMAC-signed POST events when control status changes.
              </Typography>
            </Box>

            {webhooks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No webhooks configured.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell>URL</TableCell>
                      <TableCell>Last delivery</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webhooks.map((w) => (
                      <TableRow key={w.id}>
                        <TableCell>{w.label}</TableCell>
                        <TableCell><code style={{ fontSize: '0.75rem' }}>{w.url.length > 40 ? w.url.slice(0, 40) + '…' : w.url}</code></TableCell>
                        <TableCell>
                          {w.last_delivered_at
                            ? <Tooltip title={new Date(w.last_delivered_at).toLocaleString()}><span>{new Date(w.last_delivered_at).toLocaleDateString()}</span></Tooltip>
                            : <Typography variant="caption" color="text.secondary">never</Typography>}
                        </TableCell>
                        <TableCell>
                          {w.last_status_code
                            ? (w.last_status_code >= 200 && w.last_status_code < 300
                              ? <Chip size="small" color="success" label={`HTTP ${w.last_status_code}`} />
                              : <Tooltip title={w.last_error || ''}><Chip size="small" color="error" label={`HTTP ${w.last_status_code}`} sx={{ cursor: 'help' }} /></Tooltip>)
                            : <Chip size="small" label="Pending" />}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleDeleteWebhook(w.id)}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Verifying webhook signatures</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Every event is signed with the per-webhook signing_secret. Verify before processing:
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
{`Header: X-ARIAS-Signature: sha256=<hex>
Verify:
  expected = hmac_sha256(signing_secret, request_body_bytes).hexdigest()
  signature_ok = hmac.compare_digest(expected, header.replace("sha256=", ""))`}
            </Paper>
          </AccordionDetails>
        </Accordion>
      </CardContent>

      {/* New-API-key dialog */}
      <Dialog open={createKeyOpen} onClose={() => { setCreateKeyOpen(false); setNewKeyResp(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Generate API key</DialogTitle>
        <DialogContent>
          {newKeyResp ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save this key now. It will <strong>not</strong> be shown again.
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>Label: <strong>{newKeyResp.label}</strong></Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.85rem', flex: 1, overflowX: 'auto' }}>
                  {newKeyResp.raw_key}
                </Paper>
                <Tooltip title="Copy">
                  <IconButton onClick={() => copyToClipboard(newKeyResp.raw_key)}><ContentCopy /></IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <>
              <TextField
                autoFocus
                fullWidth
                label="Label"
                placeholder="e.g. Vanta production"
                value={keyLabel}
                onChange={(e) => setKeyLabel(e.target.value)}
                helperText="Pick a name you'll recognize in your GRC tool's data-source list."
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateKeyOpen(false); setNewKeyResp(null); }}>
            {newKeyResp ? 'Done' : 'Cancel'}
          </Button>
          {!newKeyResp && (
            <Button variant="contained" disabled={creating || !keyLabel.trim()} onClick={handleCreateKey}>
              Generate
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* New-webhook dialog */}
      <Dialog open={createWhOpen} onClose={() => { setCreateWhOpen(false); setNewWhResp(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add webhook destination</DialogTitle>
        <DialogContent>
          {newWhResp ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save the signing secret in your GRC tool now. It verifies that incoming POSTs come from ARIAS.
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>Label: <strong>{newWhResp.label}</strong></Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>URL: <code>{newWhResp.url}</code></Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.85rem', flex: 1, overflowX: 'auto' }}>
                  {newWhResp.signing_secret}
                </Paper>
                <Tooltip title="Copy">
                  <IconButton onClick={() => copyToClipboard(newWhResp.signing_secret)}><ContentCopy /></IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <>
              <TextField
                autoFocus
                fullWidth
                label="Label"
                placeholder="e.g. Vanta receiver"
                value={whLabel}
                onChange={(e) => setWhLabel(e.target.value)}
                sx={{ mt: 1, mb: 2 }}
              />
              <TextField
                fullWidth
                label="URL"
                placeholder="https://your-grc-tool.com/integrations/arias/receiver"
                value={whUrl}
                onChange={(e) => setWhUrl(e.target.value)}
                helperText="HTTPS only. Must accept POST with a JSON body and X-ARIAS-Signature header."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateWhOpen(false); setNewWhResp(null); }}>
            {newWhResp ? 'Done' : 'Cancel'}
          </Button>
          {!newWhResp && (
            <Button variant="contained" disabled={whCreating || !whLabel.trim() || !whUrl.trim()} onClick={handleCreateWebhook}>
              Create
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default ComplianceIntegrations
